import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  BugReport as BugReportIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Psychology as AnalysisIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  children, 
  currentPage, 
  onPageChange 
}) => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'issues', label: 'Issues', icon: <BugReportIcon /> },
    { id: 'analyses', label: 'AI Analyses', icon: <AnalysisIcon /> },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ 
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white'
      }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          🚀 AI Sentry
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 2, py: 1 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            selected={currentPage === item.id}
            onClick={() => {
              onPageChange(item.id);
              setMobileOpen(false);
            }}
            sx={{
              borderRadius: 3,
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              '&.Mui-selected': {
                backgroundColor: '#6366f1',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4f46e5',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'translateX(4px)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: 40,
              transition: 'color 0.2s ease-in-out',
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: currentPage === item.id ? 600 : 500,
              }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 2 }}>
        <ListItemButton 
          selected={currentPage === 'settings'}
          onClick={() => {
            onPageChange('settings');
            setMobileOpen(false);
          }}
          sx={{
            borderRadius: 3,
            mb: 1,
            transition: 'all 0.2s ease-in-out',
            '&.Mui-selected': {
              backgroundColor: '#6366f1',
              color: 'white',
              '&:hover': {
                backgroundColor: '#4f46e5',
              },
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
            '&:hover': {
              backgroundColor: '#f1f5f9',
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: 40,
            transition: 'color 0.2s ease-in-out',
          }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Settings"
            primaryTypographyProps={{
              fontWeight: currentPage === 'settings' ? 600 : 500,
            }}
          />
        </ListItemButton>
        <ListItemButton 
          onClick={handleLogout}
          sx={{
            borderRadius: 3,
            mt: 1,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              transform: 'translateX(4px)',
              '& .MuiListItemIcon-root': {
                color: '#dc2626',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
            <AppBar 
        position="fixed" 
        sx={{ 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {menuItems.find(item => item.id === currentPage)?.label || 'AI Sentry Issues Explainer'}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            backgroundColor: '#f8fafc',
            padding: '8px 16px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
          }}>
            <AccountCircleIcon sx={{ color: '#6366f1' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {mounted ? (user?.username || user?.email || 'User') : 'Loading...'}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl" sx={{ py: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
